import React, {
    forwardRef,
    useRef,
    useState,
    useEffect,
    useCallback,
    useImperativeHandle,
} from 'react'
import classNames from 'classnames'
import { useTabs } from './context'
import { HiChevronLeft, HiChevronRight } from 'react-icons/hi'

const TabList = forwardRef((props, ref) => {
    const { className, children, showScrollArrows = true, ...rest } = props
    const scrollRef = useRef(null)
    const [canScrollLeft, setCanScrollLeft] = useState(false)
    const [canScrollRight, setCanScrollRight] = useState(false)

    const { value, variant } = useTabs()

    useImperativeHandle(ref, () => scrollRef.current)

    const checkScroll = useCallback(() => {
        const el = scrollRef.current
        if (!el) return
        const { scrollLeft, scrollWidth, clientWidth } = el
        const maxScrollLeft = scrollWidth - clientWidth
        setCanScrollLeft(scrollLeft > 2)
        setCanScrollRight(scrollLeft < maxScrollLeft - 2)
    }, [])

    useEffect(() => {
        const el = scrollRef.current
        if (!el) return

        checkScroll()

        el.addEventListener('scroll', checkScroll, { passive: true })

        let resizeObserver
        if (typeof ResizeObserver !== 'undefined') {
            resizeObserver = new ResizeObserver(() => {
                checkScroll()
            })
            resizeObserver.observe(el)
            Array.from(el.children).forEach((child) =>
                resizeObserver.observe(child)
            )
        }

        const handleWindowResize = () => checkScroll()
        window.addEventListener('resize', handleWindowResize)

        return () => {
            el.removeEventListener('scroll', checkScroll)
            window.removeEventListener('resize', handleWindowResize)
            if (resizeObserver) {
                resizeObserver.disconnect()
            }
        }
    }, [checkScroll, children])

    useEffect(() => {
        const el = scrollRef.current
        if (!el || !value) return
        const activeChild = el.querySelector(
            `[role="tab"][aria-selected="true"]`
        )
        if (activeChild) {
            activeChild.scrollIntoView({
                behavior: 'smooth',
                block: 'nearest',
                inline: 'nearest',
            })
        }
    }, [value])

    const handleScroll = (direction) => {
        const el = scrollRef.current
        if (!el) return
        const scrollAmount = Math.max(el.clientWidth * 0.6, 180)
        el.scrollBy({
            left: direction === 'left' ? -scrollAmount : scrollAmount,
            behavior: 'smooth',
        })
    }

    const tabListClass = classNames(
        'tab-list',
        `tab-list-${variant}`,
        className
    )

    const isScrollable = canScrollLeft || canScrollRight

    return (
        <div className="tab-list-wrapper relative flex items-center w-full min-w-0 group/tablist">
            {showScrollArrows && isScrollable && canScrollLeft && (
                <>
                    <div className="pointer-events-none absolute left-0 top-0 bottom-0 w-10 bg-gradient-to-r from-white via-white/80 to-transparent dark:from-gray-800 dark:via-gray-800/80 z-[5] rounded-l-md" />
                    <button
                        type="button"
                        aria-label="Desplazar a la izquierda"
                        className="tab-scroll-btn tab-scroll-btn-left"
                        onClick={() => handleScroll('left')}
                    >
                        <HiChevronLeft className="text-lg" />
                    </button>
                </>
            )}

            <div
                role="tablist"
                className={tabListClass}
                ref={scrollRef}
                {...rest}
            >
                {children}
            </div>

            {showScrollArrows && isScrollable && canScrollRight && (
                <>
                    <div className="pointer-events-none absolute right-0 top-0 bottom-0 w-10 bg-gradient-to-l from-white via-white/80 to-transparent dark:from-gray-800 dark:via-gray-800/80 z-[5] rounded-r-md" />
                    <button
                        type="button"
                        aria-label="Desplazar a la derecha"
                        className="tab-scroll-btn tab-scroll-btn-right"
                        onClick={() => handleScroll('right')}
                    >
                        <HiChevronRight className="text-lg" />
                    </button>
                </>
            )}
        </div>
    )
})

export default TabList

